/**
 * One recommendation round as a list entry.
 *
 * The list page only needs to answer "which rounds exist, what won, and is it
 * locked"; everything else belongs to the round detail page this card links to.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { RoundSummaryDto } from '@shared/types'

import { cn, formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { Badge } from '../ui/badge'
import { Card } from '../ui/card'

export interface RoundCardProps {
  shareToken: string
  round: RoundSummaryDto
  className?: string
}

export function RoundCard({ shareToken, round, className }: RoundCardProps) {
  const { t } = useTranslation()
  const locale = currentLocale()

  return (
    <Card className={cn('hover:border-primary/50 transition-colors', className)}>
      <Link
        to={`/e/${shareToken}/recommendations/${round.round}`}
        className="flex flex-col gap-2 p-4 sm:p-5"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">
            {t('recommend.roundLabel', { round: round.round })}
          </span>
          {round.status === 'decided' ? (
            <Badge variant="success">{t('recommend.decided')}</Badge>
          ) : round.status === 'open' ? (
            <Badge variant="secondary">{t('status.collecting')}</Badge>
          ) : null}
          {/* A partial plan is a decision the group still has to take, so the list
              flags it before anyone opens the round. */}
          {round.degraded ? (
            <Badge variant="warning">{t('recommend.planDegradedBadge')}</Badge>
          ) : null}
          {/* A queued or running round has no winner yet, so the list says so
              instead of naming the placeholder the detail page would show. */}
          {round.runStatus !== 'ready' && round.runStatus !== 'failed' ? (
            <Badge variant="default">{t('recommend.runningBadge')}</Badge>
          ) : null}
          {round.runStatus === 'failed' ? (
            <Badge variant="destructive">{t('recommend.failedBadge')}</Badge>
          ) : null}
        </div>

        <p className="text-base font-medium">
          {round.runStatus === 'failed'
            ? t('round.status.failed', { reason: round.failureReason ?? 'internal' })
            : round.runStatus !== 'ready'
              ? t('recommend.runningHint')
              : (round.primary?.districtName ?? t('common.unknown'))}
        </p>

        <p className="text-muted-foreground text-xs">
          {t('recommend.createdAt', { date: formatUnixSeconds(round.createdAt, locale) })}
          {' · '}
          {t('recommend.voteCount', { count: round.voteCount })}
          {' · '}
          {t('recommend.peopleAndUnits', {
            people: t('recommend.participantCount', { count: round.peopleCount }),
            units: t('recommend.unitCount', { count: round.unitCount }),
          })}
        </p>
      </Link>
    </Card>
  )
}
